import React from 'react';
import { useTranslation } from 'react-i18next';
import Modal, { ModalSize } from '@ssg/common/Components/Modal';
import Button from '@ssg/common/Components/Button';

interface ImageAnnotatorProps {
	visible: boolean;
	imageUrl: string; // source image to annotate - blob url or remote url
	fileName: string; // used to name the resulting File
	onClose: () => void;
	onSave: (file: File) => Promise<void> | void;
}

const ImageAnnotator: React.FC<ImageAnnotatorProps> = ({ visible, imageUrl, fileName, onClose, onSave }) => {
	const { t } = useTranslation();

	const canvasRef = React.useRef<HTMLCanvasElement>(null);
	const ctxRef = React.useRef<CanvasRenderingContext2D | null>(null);
	const baseImageRef = React.useRef<HTMLImageElement | null>(null);
	const historyRef = React.useRef<ImageData[]>([]);
	const drawingRef = React.useRef(false);

	const [color, setColor] = React.useState('#e5484d');
	const [size, setSize] = React.useState(6);
	const [ready, setReady] = React.useState(false);
	const [submitting, setSubmitting] = React.useState(false);

	// (Re)load the source image whenever the modal opens with a new image
	React.useEffect(() => {
		if (!visible || !imageUrl) return;
		setReady(false);

		const img = new Image();
		img.crossOrigin = 'anonymous';
		img.onload = () => {
			const canvas = canvasRef.current;
			const ctx = canvas?.getContext('2d');
			if (!canvas || !ctx) return;

			const maxW = 860;
			let w = img.naturalWidth;
			let h = img.naturalHeight;
			if (w > maxW) {
				h = Math.round(h * (maxW / w));
				w = maxW;
			}
			canvas.width = w;
			canvas.height = h;
			ctx.drawImage(img, 0, 0, w, h);

			ctxRef.current = ctx;
			baseImageRef.current = img;
			historyRef.current = [ctx.getImageData(0, 0, w, h)];
			setReady(true);
		};
		img.src = imageUrl;
	}, [visible, imageUrl]);

	const getPos = (e: React.PointerEvent<HTMLCanvasElement>) => {
		const canvas = canvasRef.current!;
		const rect = canvas.getBoundingClientRect();
		const scaleX = canvas.width / rect.width;
		const scaleY = canvas.height / rect.height;
		return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
	};

	const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
		const ctx = ctxRef.current;
		if (!ctx) return;
		drawingRef.current = true;
		const pos = getPos(e);

		ctx.strokeStyle = color;
		ctx.lineWidth = size;
		ctx.lineCap = 'round';
		ctx.lineJoin = 'round';
		ctx.beginPath();
		ctx.moveTo(pos.x, pos.y);
	};

	const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
		if (!drawingRef.current) return;
		const ctx = ctxRef.current;
		if (!ctx) return;
		const pos = getPos(e);
		ctx.lineTo(pos.x, pos.y);
		ctx.stroke();
	};

	const onPointerUp = () => {
		if (!drawingRef.current) return;
		drawingRef.current = false;
		const ctx = ctxRef.current;
		if (!ctx) return;
		historyRef.current.push(ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height));
		if (historyRef.current.length > 30) historyRef.current.shift();
	};

	const undo = () => {
		const ctx = ctxRef.current;
		if (!ctx || historyRef.current.length <= 1) return;
		historyRef.current.pop();
		ctx.putImageData(historyRef.current[historyRef.current.length - 1], 0, 0);
	};

	const clearMarkings = () => {
		const ctx = ctxRef.current;
		const img = baseImageRef.current;
		if (!ctx || !img) return;
		ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
		ctx.drawImage(img, 0, 0, ctx.canvas.width, ctx.canvas.height);
		historyRef.current = [ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height)];
	};

	const handleCancel = () => {
		// discard markings - next open will reload the original source image fresh
		onClose();
	};

	const handleSave = () => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		canvas.toBlob(async blob => {
			if (!blob) return;
			const file = new File([blob], fileName, { type: 'image/png' });
			try {
				setSubmitting(true);
				await onSave(file);
				onClose();
			} catch (error) {
				console.error('Failed to save marked image', error);
			} finally {
				setSubmitting(false);
			}
		}, 'image/png');
	};

	return (
		<Modal
			visible={visible}
			close={handleCancel}
			size={ModalSize.LARGE}
			title={t('reports.imageAnnotator.title')}
			body={
				<div className="space-y-3">
					<div className="flex flex-wrap items-center gap-4 rounded border border-gray-200 p-2">
						<label className="flex items-center gap-2 text-xs text-gray-500">
							{t('reports.imageAnnotator.color')}
							<input
								type="color"
								value={color}
								onChange={e => setColor(e.target.value)}
								className="h-8 w-8 rounded border border-gray-300"
							/>
						</label>

						<label className="flex items-center gap-2 text-xs text-gray-500">
							{t('reports.imageAnnotator.thickness')}
							<input type="range" min={2} max={30} value={size} onChange={e => setSize(Number(e.target.value))} />
						</label>

						<div className="mx-1 h-6 w-px bg-gray-200" />

						<button type="button" onClick={undo} className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs">
							{t('reports.imageAnnotator.undo')}
						</button>
						<button type="button" onClick={clearMarkings} className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs">
							{t('reports.imageAnnotator.clear')}
						</button>
					</div>

					<div className="flex max-h-[60vh] items-center justify-center overflow-auto rounded border border-gray-200 bg-gray-50">
						<canvas
							ref={canvasRef}
							className="touch-none"
							onPointerDown={onPointerDown}
							onPointerMove={onPointerMove}
							onPointerUp={onPointerUp}
							onPointerLeave={onPointerUp}
						/>
					</div>
				</div>
			}
			footer={
				<>
					<Button onClick={handleCancel} disabled={submitting}>
						{t('common.cancel')}
					</Button>
					<Button primary disabled={!ready || submitting} loading={submitting} onClick={handleSave}>
						{t('reports.imageAnnotator.save')}
					</Button>
				</>
			}
		/>
	);
};

export default ImageAnnotator;